import type { Sitzung } from '@nac2s/shared'
import { apiFetch } from './client.js'

/** Die Gestalt steht in `packages/shared` — hier nur der Aufruf. */
export type SessionInfo = Sitzung

/** Wer ist angemeldet. Wirft `ApiError` mit Status 401, wenn niemand. */
export function ladeSitzung(): Promise<SessionInfo> {
  return apiFetch<SessionInfo>('/api/session/me')
}

/**
 * Löst das Einmal-Token aus der Sitzungsübergabe ein.
 *
 * Der Endpunkt gehört Better Auth — es setzt dabei das signierte
 * Sitzungscookie selbst (siehe `handoffPlugin.ts` im Backend).
 */
export function loeseUebergabeEin(token: string): Promise<{ appUserId: string }> {
  return apiFetch<{ appUserId: string }>('/api/auth/nac2s/handoff/redeem', {
    method: 'POST',
    body: JSON.stringify({ token }),
  })
}

/**
 * Anmeldung mit E-Mail und Passwort (ADR-0004 Nachtrag 5).
 *
 * Der zweite Weg hinein, für Geräte ohne App. Der Endpunkt gehört Better Auth;
 * Zähler und Kontosperre liegen davor in `app.ts`.
 */
export function anmelden(email: string, passwort: string): Promise<{ zweiterFaktor: boolean }> {
  return apiFetch<{ twoFactorRedirect?: boolean }>('/api/auth/sign-in/email', {
    method: 'POST',
    body: JSON.stringify({ email, password: passwort }),
  }).then((antwort) => ({ zweiterFaktor: antwort?.twoFactorRedirect === true }))
}

export function abmelden(): Promise<void> {
  return apiFetch('/api/auth/sign-out', { method: 'POST', body: '{}' }).then(() => undefined)
}

/**
 * Zugangslink einlösen: Passwort setzen und gleich angemeldet sein.
 *
 * Wie bei der Übergabe setzt Better Auth das Sitzungscookie selbst.
 */
export function loeseZugangEin(token: string, passwort: string): Promise<{ appUserId: string }> {
  return apiFetch<{ appUserId: string }>('/api/auth/nac2s/zugang/einloesen', {
    method: 'POST',
    body: JSON.stringify({ token, passwort }),
  })
}

/**
 * Zweiter Faktor (ADR-0004 Nachtrag 6) — die Endpunkte des Better-Auth-Plugins.
 *
 * Einrichten läuft in zwei Schritten: `zweiFaktorAnlegen` liefert die
 * `otpauth`-Adresse und die Wiederherstellungscodes, ist aber noch **nicht**
 * scharf. Erst `zweiFaktorBestaetigen` mit einem Code aus der App schaltet ihn
 * ein. So merkt niemand erst bei der nächsten Anmeldung, dass er die falsche
 * App eingerichtet hat.
 */
export function zweiFaktorAnlegen(
  passwort: string,
): Promise<{ totpURI: string; backupCodes: string[] }> {
  return apiFetch<{ totpURI: string; backupCodes: string[] }>('/api/auth/two-factor/enable', {
    method: 'POST',
    body: JSON.stringify({ password: passwort }),
  })
}

export function zweiFaktorBestaetigen(code: string): Promise<unknown> {
  return apiFetch('/api/auth/two-factor/verify-totp', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
}

export function zweiFaktorAbschalten(passwort: string): Promise<unknown> {
  return apiFetch('/api/auth/two-factor/disable', {
    method: 'POST',
    body: JSON.stringify({ password: passwort }),
  })
}
